import React, { useState } from 'react';
import {
    Box, Paper, Typography, Button, IconButton, Chip, Alert,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Dialog, DialogTitle, DialogContent, DialogActions, TextField, Tooltip,
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    Add as AddIcon,
    ContentCopy as ContentCopyIcon,
    UpgradeOutlined as RevBumpIcon,
    Archive as ArchiveIcon,
    FileCopy as CloneIcon,
} from '@mui/icons-material';
import { revBumpPart, clonePart, archivePart, createFeature } from './partsApi';
import { apiErrorMessage } from '../../api/apiError';

// ---------------------------------------------------------------------------
// PartDetail — features list w/ specs; rev-bump / clone / archive actions
// (confirm dialogs); duplicate-feature-with-changed-specs flow suggesting the
// suffix convention (doc §2.2 owner decision 2).
// ---------------------------------------------------------------------------

const inputSx = {
    '& .MuiOutlinedInput-root': { color: 'rgba(255, 255, 255, 0.9)' },
    '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.6)' },
};

function specSummary(feature) {
    const parts = [];
    if (feature.lsl !== null && feature.lsl !== undefined) parts.push(`LSL ${feature.lsl}`);
    if (feature.nominal !== null && feature.nominal !== undefined) parts.push(`Target ${feature.nominal}`);
    if (feature.usl !== null && feature.usl !== undefined) parts.push(`USL ${feature.usl}`);
    if (feature.unit) parts.push(feature.unit);
    return parts.length ? parts.join(' · ') : 'No spec set';
}

// ---------------------------------------------------------------------------
// Rev-bump dialog
// ---------------------------------------------------------------------------
function RevBumpDialog({ open, part, onClose, onDone }) {
    const [revision, setRevision] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    const handleClose = () => { setRevision(''); setError(null); onClose(); };

    const handleSubmit = async () => {
        if (!revision.trim()) { setError('Enter the new revision (e.g. "B").'); return; }
        setSubmitting(true);
        setError(null);
        try {
            const { ok, status, data } = await revBumpPart(part.id, revision.trim());
            if (!ok) {
                setError(status === 409 ? 'That revision already exists for this part.' : apiErrorMessage(status, data, 'Could not create the revision.'));
                return;
            }
            onDone(data);
            handleClose();
        } catch (e) {
            setError('Network error. Try again.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth PaperProps={{ sx: { backgroundColor: '#1a1f26', border: '1px solid rgba(255,255,255,0.12)' } }}>
            <DialogTitle sx={{ color: 'rgba(255,255,255,0.9)' }}>Rev-bump {part?.part_number}</DialogTitle>
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.55)' }}>
                    Creates a new revision with a fresh copy of every feature and stream config.
                    Rev {part?.revision || '—'} keeps its measurements and stays editable — nothing on it changes.
                </Typography>
                <TextField
                    label="New revision" size="small" fullWidth sx={inputSx} autoFocus
                    value={revision} onChange={(e) => setRevision(e.target.value)}
                    disabled={submitting} inputProps={{ maxLength: 40 }}
                />
                {error && <Alert severity="error" sx={{ py: 0.5 }}>{error}</Alert>}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={handleClose} disabled={submitting} sx={{ color: 'rgba(255,255,255,0.6)' }}>Cancel</Button>
                <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
                    {submitting ? 'Creating…' : 'Create revision'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

// ---------------------------------------------------------------------------
// Clone dialog
// ---------------------------------------------------------------------------
function CloneDialog({ open, part, onClose, onDone }) {
    const [partNumber, setPartNumber] = useState('');
    const [name, setName] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    const handleClose = () => { setPartNumber(''); setName(''); setError(null); onClose(); };

    const handleSubmit = async () => {
        if (!partNumber.trim()) { setError('Enter a part number for the clone.'); return; }
        setSubmitting(true);
        setError(null);
        try {
            const { ok, status, data } = await clonePart(part.id, { partNumber: partNumber.trim(), name: name.trim() || undefined });
            if (!ok) {
                setError(status === 409 ? 'That part number/revision already exists.' : apiErrorMessage(status, data, 'Could not clone the part.'));
                return;
            }
            onDone(data);
            handleClose();
        } catch (e) {
            setError('Network error. Try again.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth PaperProps={{ sx: { backgroundColor: '#1a1f26', border: '1px solid rgba(255,255,255,0.12)' } }}>
            <DialogTitle sx={{ color: 'rgba(255,255,255,0.9)' }}>Clone {part?.part_number}</DialogTitle>
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.55)' }}>
                    Copies this part's features and default streams into a brand-new part — a genuinely
                    different part that happens to look similar, not a new revision.
                </Typography>
                <TextField
                    label="New part number" size="small" fullWidth sx={inputSx} autoFocus
                    value={partNumber} onChange={(e) => setPartNumber(e.target.value)}
                    disabled={submitting} inputProps={{ maxLength: 120 }}
                />
                <TextField
                    label="Name (optional)" size="small" fullWidth sx={inputSx}
                    value={name} onChange={(e) => setName(e.target.value)}
                    disabled={submitting} inputProps={{ maxLength: 200 }}
                />
                {error && <Alert severity="error" sx={{ py: 0.5 }}>{error}</Alert>}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={handleClose} disabled={submitting} sx={{ color: 'rgba(255,255,255,0.6)' }}>Cancel</Button>
                <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
                    {submitting ? 'Cloning…' : 'Clone part'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

// ---------------------------------------------------------------------------
// Archive confirm dialog
// ---------------------------------------------------------------------------
function ArchiveDialog({ open, part, onClose, onDone }) {
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    const handleSubmit = async () => {
        setSubmitting(true);
        setError(null);
        try {
            const { ok, status, data } = await archivePart(part.id);
            if (!ok) { setError(apiErrorMessage(status, data, 'Could not archive the part.')); return; }
            onDone(data);
            onClose();
        } catch (e) {
            setError('Network error. Try again.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { backgroundColor: '#1a1f26', border: '1px solid rgba(255,255,255,0.12)' } }}>
            <DialogTitle sx={{ color: 'rgba(255,255,255,0.9)' }}>Archive {part?.part_number}?</DialogTitle>
            <DialogContent sx={{ pt: 1 }}>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.55)' }}>
                    This hides the part from the list. Its measurements and history are kept — nothing is deleted.
                </Typography>
                {error && <Alert severity="error" sx={{ mt: 2, py: 0.5 }}>{error}</Alert>}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose} disabled={submitting} sx={{ color: 'rgba(255,255,255,0.6)' }}>Cancel</Button>
                <Button variant="contained" color="error" onClick={handleSubmit} disabled={submitting}>
                    {submitting ? 'Archiving…' : 'Archive'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

// ---------------------------------------------------------------------------
// New / duplicate feature dialog — suggests the suffix convention (doc §2.2)
// when the user changes spec fields on a duplicated feature.
// ---------------------------------------------------------------------------
function FeatureDialog({ open, part, sourceFeature, onClose, onDone }) {
    const isDuplicate = !!sourceFeature;
    const [name, setName] = useState('');
    const [usl, setUsl] = useState('');
    const [lsl, setLsl] = useState('');
    const [nominal, setNominal] = useState('');
    const [unit, setUnit] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    React.useEffect(() => {
        if (open && sourceFeature) {
            setName(sourceFeature.name || '');
            setUsl(sourceFeature.usl ?? '');
            setLsl(sourceFeature.lsl ?? '');
            setNominal(sourceFeature.nominal ?? '');
            setUnit(sourceFeature.unit || '');
        } else if (open) {
            setName(''); setUsl(''); setLsl(''); setNominal(''); setUnit('');
        }
    }, [open, sourceFeature]);

    const specChanged = isDuplicate && (
        String(usl) !== String(sourceFeature.usl ?? '') ||
        String(lsl) !== String(sourceFeature.lsl ?? '') ||
        String(nominal) !== String(sourceFeature.nominal ?? '')
    );
    const nameUnchanged = isDuplicate && name.trim() === (sourceFeature.name || '').trim();
    const suggestSuffix = specChanged && nameUnchanged;
    const suggestedName = isDuplicate ? `${(sourceFeature.name || '').trim()} — Op2` : '';

    const handleClose = () => { setError(null); onClose(); };

    const toNum = (v) => (v === '' || v === null || v === undefined ? null : (Number.isFinite(Number(v)) ? Number(v) : null));

    const handleSubmit = async () => {
        if (!name.trim()) { setError('Feature name is required.'); return; }
        setSubmitting(true);
        setError(null);
        try {
            const { ok, status, data } = await createFeature(part.id, {
                name: name.trim(), usl: toNum(usl), lsl: toNum(lsl), nominal: toNum(nominal), unit: unit.trim() || null,
            });
            if (!ok) {
                if (status === 409 || data?.code === 'CONFLICT') {
                    setError(`A feature named "${name.trim()}" already exists on this part. ${isDuplicate ? `Try a distinguishing suffix, e.g. "${suggestedName}".` : ''}`);
                } else {
                    setError(apiErrorMessage(status, data, 'Could not create the feature.'));
                }
                return;
            }
            onDone(data);
            handleClose();
        } catch (e) {
            setError('Network error. Try again.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth PaperProps={{ sx: { backgroundColor: '#1a1f26', border: '1px solid rgba(255,255,255,0.12)' } }}>
            <DialogTitle sx={{ color: 'rgba(255,255,255,0.9)' }}>{isDuplicate ? 'Duplicate feature' : 'New feature'}</DialogTitle>
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                <TextField label="Feature name" size="small" fullWidth sx={inputSx} autoFocus
                    value={name} onChange={(e) => setName(e.target.value)} disabled={submitting} inputProps={{ maxLength: 160 }} />
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                    <TextField label="LSL" size="small" fullWidth sx={inputSx} type="number" value={lsl} onChange={(e) => setLsl(e.target.value)} disabled={submitting} />
                    <TextField label="Nominal" size="small" fullWidth sx={inputSx} type="number" value={nominal} onChange={(e) => setNominal(e.target.value)} disabled={submitting} />
                    <TextField label="USL" size="small" fullWidth sx={inputSx} type="number" value={usl} onChange={(e) => setUsl(e.target.value)} disabled={submitting} />
                </Box>
                <TextField label="Unit" size="small" fullWidth sx={inputSx} value={unit} onChange={(e) => setUnit(e.target.value)} disabled={submitting} inputProps={{ maxLength: 24 }} />
                {suggestSuffix && (
                    <Alert severity="info" sx={{ py: 0.5 }}>
                        The spec changed but the name didn't — since this is the same callout measured at a
                        different process point, a distinguishing suffix keeps both features straight, e.g.{' '}
                        <strong>"{suggestedName}"</strong>.
                    </Alert>
                )}
                {error && <Alert severity="error" sx={{ py: 0.5 }}>{error}</Alert>}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={handleClose} disabled={submitting} sx={{ color: 'rgba(255,255,255,0.6)' }}>Cancel</Button>
                <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
                    {submitting ? 'Saving…' : 'Save feature'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

// ---------------------------------------------------------------------------
// Main
// ---------------------------------------------------------------------------
function PartDetail({ part, onBack, onOpenFeature, onRevBumped, onCloned, onArchived, onFeatureCreated }) {
    const [dialog, setDialog] = useState(null); // 'revbump' | 'clone' | 'archive' | 'feature' | 'duplicate'
    const [duplicateSource, setDuplicateSource] = useState(null);

    const features = Array.isArray(part?.features) ? part.features : [];

    const defaultStream = (feature) => {
        const streams = Array.isArray(feature.streams) ? feature.streams : [];
        return streams.find((s) => s.process_label === '') || streams[0] || null;
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <IconButton size="small" onClick={onBack} title="Back to parts list" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                    <ArrowBackIcon fontSize="small" />
                </IconButton>
                <Box sx={{ mr: 'auto' }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                        {part?.part_number}
                        {part?.revision && <Chip label={`Rev ${part.revision}`} size="small" sx={{ backgroundColor: 'rgba(144,202,249,0.12)', color: '#90caf9' }} />}
                    </Typography>
                    {part?.name && <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.45)' }}>{part.name}</Typography>}
                </Box>
                <Tooltip title="Copy features into a new revision">
                    <Button size="small" startIcon={<RevBumpIcon />} onClick={() => setDialog('revbump')} sx={{ color: '#90caf9' }}>
                        Rev bump
                    </Button>
                </Tooltip>
                <Tooltip title="Copy into a new, similar part">
                    <Button size="small" startIcon={<CloneIcon />} onClick={() => setDialog('clone')} sx={{ color: '#90caf9' }}>
                        Clone
                    </Button>
                </Tooltip>
                <Button size="small" color="error" startIcon={<ArchiveIcon />} onClick={() => setDialog('archive')}>
                    Archive
                </Button>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="subtitle2" sx={{ color: 'rgba(255,255,255,0.7)', fontWeight: 600, mr: 'auto' }}>
                    Features
                </Typography>
                <Button size="small" startIcon={<AddIcon />} onClick={() => { setDuplicateSource(null); setDialog('feature'); }}>
                    New feature
                </Button>
            </Box>

            <TableContainer component={Paper} sx={{ backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            {['Feature', 'Spec', ''].map((h) => (
                                <TableCell key={h} sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', borderBottom: '1px solid rgba(255,255,255,0.1)', py: 0.75, px: 1.5 }}>{h}</TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {features.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={3} sx={{ color: 'rgba(255,255,255,0.4)', textAlign: 'center', py: 3 }}>
                                    No features yet.
                                </TableCell>
                            </TableRow>
                        ) : features.map((feature) => (
                            <TableRow
                                key={feature.id}
                                hover
                                sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'rgba(144,202,249,0.05)' } }}
                            >
                                <TableCell
                                    onClick={() => onOpenFeature(feature, defaultStream(feature))}
                                    sx={{ color: 'rgba(255,255,255,0.9)', fontWeight: 600, fontSize: '0.85rem', py: 1, px: 1.5 }}
                                >
                                    {feature.name}
                                </TableCell>
                                <TableCell
                                    onClick={() => onOpenFeature(feature, defaultStream(feature))}
                                    sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.8rem', py: 1, px: 1.5 }}
                                >
                                    {specSummary(feature)}
                                </TableCell>
                                <TableCell sx={{ py: 1, px: 1.5, textAlign: 'right' }}>
                                    <Tooltip title="Duplicate with different specs">
                                        <IconButton size="small" onClick={() => { setDuplicateSource(feature); setDialog('feature'); }} sx={{ color: 'rgba(255,255,255,0.4)' }}>
                                            <ContentCopyIcon fontSize="inherit" />
                                        </IconButton>
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <RevBumpDialog open={dialog === 'revbump'} part={part} onClose={() => setDialog(null)} onDone={onRevBumped} />
            <CloneDialog open={dialog === 'clone'} part={part} onClose={() => setDialog(null)} onDone={onCloned} />
            <ArchiveDialog open={dialog === 'archive'} part={part} onClose={() => setDialog(null)} onDone={onArchived} />
            <FeatureDialog
                open={dialog === 'feature'}
                part={part}
                sourceFeature={duplicateSource}
                onClose={() => setDialog(null)}
                onDone={onFeatureCreated}
            />
        </Box>
    );
}

export default PartDetail;
